import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import Navbar from './Navbar';
import Footer from './Footer';

// ─── DATA ────────────────────────────────────────────────────────────────────
const STUDIO_CATEGORIES = [
  { id: 'recording', title: 'Recording Session', desc: 'Premium vocal and instrumental tracking. Pricing varies based on your project requirements.', price: 'Starts ₹2,500', icon: '🎙️' },
  { id: 'practice', title: 'Practice Session', desc: 'Professional 4-hour studio slots for rehearsals and performance prep.', price: 'Starts ₹800', icon: '🎸' },
];
const PRACTICE_SUB_SERVICES = [
  { id: 'live', title: 'Live Practice', desc: 'Full band or solo rehearsal with professional backline and monitors.', price: '₹1,500 / 4-hr slot' },
  { id: 'karaoke', title: 'Karaoke Practice', desc: 'Perfect your vocals with high-end microphones and your favorite tracks.', price: '₹800 / 4-hr slot' },
];
const DATES = Array.from({ length: 14 }).map((_, i) => {
  const d = new Date(); d.setDate(d.getDate() + i + 1);
  return { date: d.getDate(), day: d.toLocaleDateString('en-US', { weekday: 'short' }), full: d };
});
const TIMESLOTS = ['10:00 AM', '02:30 PM', '07:00 PM'];

// ─── LIGHT THEME PALETTE ──────────────────────────────────────────────────────
// Warm ivory + deep amber gold + ink charcoal
// BG: #FAF6EF  Surface: #F2ECE1  Border: rgba(180,140,60,0.18)
// Gold: #B8860B  Gold light: #D4A017  Text: #1C1409  Muted: rgba(28,20,9,0.42)

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @keyframes grain {
    0%,100%{transform:translate(0,0)}10%{transform:translate(-2%,-3%)}20%{transform:translate(3%,1%)}
    30%{transform:translate(-1%,4%)}40%{transform:translate(4%,-2%)}50%{transform:translate(-3%,3%)}
    60%{transform:translate(2%,-4%)}70%{transform:translate(-4%,1%)}80%{transform:translate(3%,3%)}90%{transform:translate(-1%,-2%)}
  }
  @keyframes floatUp {
    0%   { transform: translateY(100vh) translateX(0px); opacity: 0; }
    5%   { opacity: 0.7; }
    95%  { opacity: 0.08; }
    100% { transform: translateY(-40px) translateX(20px); opacity: 0; }
  }
  @keyframes shimmerText {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes breathe {
    0%,100% { transform: scale(1); }
    50%     { transform: scale(1.012); }
  }
  @keyframes orbDrift {
    0%   { transform: translateY(0px); }
    50%  { transform: translateY(-6px); }
    100% { transform: translateY(0px); }
  }
  @keyframes drawCircle {
    from { stroke-dashoffset: 201; }
    to   { stroke-dashoffset: 0; }
  }
  @keyframes drawCheck {
    from { stroke-dashoffset: 50; }
    to   { stroke-dashoffset: 0; }
  }
  @keyframes letterDrop {
    from { opacity: 0; transform: translateY(-16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sonarPing {
    0%   { transform: scale(0.6); opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes particleBurst {
    0%   { transform: translate(0,0) scale(1); opacity: 1; }
    100% { transform: translate(var(--px), var(--py)) scale(0); opacity: 0; }
  }
  @keyframes goldPulse {
    0%,100% { box-shadow: 0 0 0px rgba(184,134,11,0); }
    50%     { box-shadow: 0 0 28px rgba(184,134,11,0.18); }
  }
  /* Floating mesh orbs */
  @keyframes meshDrift1 {
    0%,100% { transform: translate(0,0) scale(1); }
    33%     { transform: translate(40px,-30px) scale(1.06); }
    66%     { transform: translate(-20px,50px) scale(0.97); }
  }
  @keyframes meshDrift2 {
    0%,100% { transform: translate(0,0) scale(1); }
    40%     { transform: translate(-55px,25px) scale(1.04); }
    70%     { transform: translate(30px,-40px) scale(1.08); }
  }
  @keyframes meshDrift3 {
    0%,100% { transform: translate(0,0) scale(1); }
    30%     { transform: translate(25px,60px) scale(0.95); }
    60%     { transform: translate(-45px,-20px) scale(1.05); }
  }
  @keyframes meshDrift4 {
    0%,100% { transform: translate(0,0); }
    50%     { transform: translate(35px,-55px); }
  }
  @keyframes lineFlow {
    0%   { stroke-dashoffset: 800; opacity: 0; }
    10%  { opacity: 0.4; }
    90%  { opacity: 0.4; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }
  @keyframes scanLineLight {
    0%   { transform: translateX(-200%) translateY(-200%) rotate(35deg); }
    100% { transform: translateX(200%) translateY(200%) rotate(35deg); }
  }
  @keyframes crosshairSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .booking-grain::after {
    content: '';
    position: fixed;
    inset: -50%;
    width: 200%;
    height: 200%;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.028;
    animation: grain 0.5s steps(2) infinite;
    pointer-events: none;
    z-index: 1;
  }

  .shimmer-gold {
    background: linear-gradient(90deg,
      #9A6E00 0%, #9A6E00 30%,
      #D4A017 45%, #F5D060 52%, #D4A017 58%,
      #9A6E00 70%, #9A6E00 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmerText 7s linear infinite;
  }

  .step-section {
    opacity: 0;
    transform: translateY(36px);
    transition: opacity 0.95s cubic-bezier(0.16,1,0.3,1), transform 0.95s cubic-bezier(0.16,1,0.3,1);
  }
  .step-section.visible {
    opacity: 1;
    transform: translateY(0);
  }
  .step-section.past {
    opacity: 0.45;
    transform: scale(0.974) translateY(-4px);
    transition: opacity 0.85s ease, transform 0.85s cubic-bezier(0.16,1,0.3,1);
  }

  .date-cell { position: relative; overflow: hidden; }
  .date-sonar {
    position: absolute;
    inset: 0;
    border-radius: 2px;
    border: 1px solid #B8860B;
    pointer-events: none;
    opacity: 0;
    transform: scale(0.6);
  }
  .date-cell:hover:not(.date-active) .date-sonar {
    animation: sonarPing 0.55s ease-out forwards;
  }

  .slot-pill {
    position: relative;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.5s cubic-bezier(0.16,1,0.3,1), border-color 0.3s, background 0.3s, color 0.3s;
  }
  .slot-pill.active-slot {
    animation: goldPulse 2.5s ease-in-out infinite;
  }

  .confirm-btn {
    position: relative;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.3s;
    background: #1C1409;
    color: #FAF6EF;
    border: none;
    cursor: pointer;
    font-family: "'DM Sans', sans-serif";
    text-transform: uppercase;
    letter-spacing: 4px;
  }
  .confirm-btn::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(184,134,11,0.25), transparent);
    transform: skewX(-20deg);
  }
  .confirm-btn:hover::before {
    left: 160%;
    transition: left 0.65s ease;
  }
  .confirm-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 48px rgba(184,134,11,0.22); }
  .confirm-btn:active { transform: scale(0.98); }

  /* Mesh lines SVG animation */
  .mesh-line {
    stroke-dasharray: 800;
    animation: lineFlow 8s ease-in-out infinite;
  }
`;

// ─── PIANO BACKGROUND ─────────────────────────────────────────────────────────
function PianoBackground() {
  const [activeKeys, setActiveKeys] = useState([]);
  const [numWhite, setNumWhite] = useState(window.innerWidth < 768 ? 12 : 28);
  const BLACK_PATTERN = [1, 1, 0, 1, 1, 1, 0]; // C# D# _ F# G# A# _

  useEffect(() => {
    const handleResize = () => setNumWhite(window.innerWidth < 768 ? 12 : 28);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const numKeys = 1 + Math.floor(Math.random() * 3);
      const keys = Array.from({ length: numKeys }, () => Math.floor(Math.random() * numWhite));
      setActiveKeys(keys);
      setTimeout(() => setActiveKeys([]), 400);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Generate floating notes
  const notes = Array.from({ length: 30 }, (_, i) => ({
    note: ['♩', '♪', '♫', '♬', '𝅗𝅥'][i % 5],
    left: 3 + (i * 3.3) % 94,
    duration: 12 + Math.random() * 16,
    delay: Math.random() * 20,
    size: 16 + Math.random() * 12,
  }));

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {/* Ivory canvas */}
      <div style={{ position: 'absolute', inset: 0, background: '#FAF6EF' }} />

      {/* Warm ambient glow */}
      <div style={{
        position: 'absolute',
        top: '-10%', left: '20%',
        width: '60vw', height: '60vw',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,160,23,0.07) 0%, transparent 65%)',
      }} />

      {/* CSS for note animations */}
      <style>{`
        @keyframes noteRise {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
          5%   { opacity: 0.25; }
          50%  { opacity: 0.18; }
          95%  { opacity: 0.06; }
          100% { transform: translateY(-100vh) rotate(15deg); opacity: 0; }
        }
        @keyframes keyGlow {
          0%   { box-shadow: 0 0 0 rgba(184,134,11,0); }
          50%  { box-shadow: 0 0 20px rgba(184,134,11,0.3), 0 -8px 30px rgba(212,160,23,0.15); }
          100% { box-shadow: 0 0 0 rgba(184,134,11,0); }
        }
      `}</style>

      {/* Floating music notes */}
      {notes.map((n, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            left: `${n.left}%`,
            bottom: '80px',
            fontSize: n.size,
            color: '#B8860B',
            opacity: 0,
            animation: `noteRise ${n.duration}s linear ${n.delay}s infinite`,
            willChange: 'transform, opacity',
            userSelect: 'none',
          }}
        >
          {n.note}
        </span>
      ))}

      {/* Piano keyboard at bottom */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: 80,
        display: 'flex',
        justifyContent: 'center',
        opacity: 0.12,
      }}>
        <div style={{ display: 'flex', position: 'relative', height: '100%' }}>
          {/* White keys */}
          {Array.from({ length: numWhite }, (_, i) => {
            const isActive = activeKeys.includes(i);
            return (
              <div
                key={`w-${i}`}
                style={{
                  width: 36,
                  height: '100%',
                  background: isActive
                    ? 'linear-gradient(to top, rgba(212,160,23,0.35), rgba(250,246,239,0.95))'
                    : 'linear-gradient(to top, #E8DCC8, #FAF6EF)',
                  borderLeft: '1px solid rgba(184,134,11,0.15)',
                  borderRight: '1px solid rgba(184,134,11,0.15)',
                  borderTop: '1px solid rgba(184,134,11,0.2)',
                  borderRadius: '0 0 3px 3px',
                  transition: 'background 0.15s ease',
                  animation: isActive ? 'keyGlow 0.6s ease-out' : 'none',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            );
          })}

          {/* Black keys */}
          {Array.from({ length: numWhite - 1 }, (_, i) => {
            const patIdx = i % 7;
            if (!BLACK_PATTERN[patIdx]) return null;
            const isActive = activeKeys.includes(i) || activeKeys.includes(i + 1);
            return (
              <div
                key={`b-${i}`}
                style={{
                  position: 'absolute',
                  left: (i + 1) * 36 - 11,
                  top: 0,
                  width: 22,
                  height: '55%',
                  background: isActive
                    ? 'linear-gradient(to top, rgba(184,134,11,0.6), #2A1F0A)'
                    : 'linear-gradient(to top, #1C1409, #3A2A12)',
                  borderRadius: '0 0 3px 3px',
                  zIndex: 2,
                  transition: 'background 0.15s ease',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Subtle grid */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.025 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#8B6914" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </div>
  );
}

// ─── AMBIENT PARTICLES (light) ────────────────────────────────────────────────
function AmbientParticles() {
  const particles = Array.from({ length: 14 }, (_, i) => ({
    x: 5 + Math.random() * 90,
    duration: 16 + Math.random() * 20,
    delay: Math.random() * 24,
    size: 1.5 + Math.random() * 2,
  }));

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2, overflow: 'hidden' }}>
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            bottom: '-4px',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: 'rgba(184,134,11,0.55)',
            animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ─── PARTICLE BURST ───────────────────────────────────────────────────────────
function ParticleBurst({ x, y }) {
  const particles = Array.from({ length: 22 }, (_, i) => {
    const angle = (i / 22) * 360 + Math.random() * 16;
    const dist = 35 + Math.random() * 65;
    return {
      px: Math.cos((angle * Math.PI) / 180) * dist,
      py: Math.sin((angle * Math.PI) / 180) * dist,
      size: 1.5 + Math.random() * 3,
      delay: Math.random() * 0.12,
      gold: i % 3 === 0,
    };
  });

  return (
    <div style={{ position: 'fixed', left: x, top: y, pointerEvents: 'none', zIndex: 9999 }}>
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.gold ? '#D4A017' : '#B8860B',
            '--px': `${p.px}px`,
            '--py': `${p.py}px`,
            animation: `particleBurst 0.75s cubic-bezier(0.16,1,0.3,1) ${p.delay}s forwards`,
            transform: 'translate(-50%,-50%)',
          }}
        />
      ))}
    </div>
  );
}

// ─── SERVICE CARDS ────────────────────────────────────────────────────────────
function ServiceCards({ category, onSelect }) {
  const [burst, setBurst] = useState(null);
  const tilt3dRef = useRef({});

  const handleClick = (cat, e) => {
    setBurst({ x: e.clientX, y: e.clientY });
    setTimeout(() => setBurst(null), 900);
    onSelect(cat);
  };

  const onMouseMove = (id, e) => {
    const el = tilt3dRef.current[id];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 14;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -14;
    el.style.transform = `perspective(1000px) rotateY(${x}deg) rotateX(${y}deg) translateZ(14px)`;
    el.style.transition = 'transform 0.05s';
  };

  const onMouseLeave = (id) => {
    const el = tilt3dRef.current[id];
    if (!el) return;
    el.style.transition = 'transform 0.65s cubic-bezier(0.16,1,0.3,1)';
    el.style.transform = '';
    setTimeout(() => { if (el) el.style.transition = ''; }, 650);
  };

  const hasSelection = !!category;

  return (
    <>
      {burst && <ParticleBurst x={burst.x} y={burst.y} />}

      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', width: '100%' }}>
        {STUDIO_CATEGORIES.map((cat) => {
          const isSelected = category?.id === cat.id;
          const isUnselected = hasSelection && !isSelected;

          return (
            <motion.div
              key={cat.id}
              ref={(el) => { tilt3dRef.current[cat.id] = el; }}
              onMouseMove={(e) => !isUnselected && onMouseMove(cat.id, e)}
              onMouseLeave={() => onMouseLeave(cat.id)}
              onClick={(e) => handleClick(cat, e)}
              animate={{
                flex: isSelected ? 3 : isUnselected ? 0.42 : 1,
                opacity: isUnselected ? 0.6 : 1,
              }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              style={{
                minWidth: 0,
                borderRadius: 4,
                cursor: 'pointer',
                border: isSelected
                  ? '1px solid rgba(184,134,11,0.6)'
                  : '1px solid rgba(184,134,11,0.14)',
                background: isSelected
                  ? 'rgba(212,160,23,0.07)'
                  : 'rgba(255,251,244,0.72)',
                backdropFilter: 'blur(8px)',
                boxShadow: isSelected
                  ? '0 8px 40px rgba(184,134,11,0.12), 0 2px 8px rgba(0,0,0,0.06)'
                  : '0 2px 16px rgba(0,0,0,0.05), 0 1px 4px rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 300,
                animation: isUnselected ? 'breathe 3.5s ease-in-out infinite' : 'none',
                transition: 'box-shadow 0.4s, border-color 0.4s',
              }}
            >
              {/* Selection flash */}
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 1 }}
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'radial-gradient(ellipse at center, rgba(212,160,23,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Breathing border on unselected */}
              {isUnselected && (
                <motion.div
                  animate={{ borderColor: ['rgba(184,134,11,0)', 'rgba(184,134,11,0.22)', 'rgba(184,134,11,0)'] }}
                  transition={{ duration: 2.8, repeat: Infinity }}
                  style={{
                    position: 'absolute', inset: 0,
                    border: '1px solid transparent',
                    borderRadius: 4,
                    pointerEvents: 'none',
                  }}
                />
              )}

              <AnimatePresence mode="wait">
                {!isUnselected ? (
                  <motion.div
                    key="expanded"
                    initial={isSelected && hasSelection ? { opacity: 0, y: 12 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    style={{ padding: '52px 40px', textAlign: 'center' }}
                  >
                    <motion.div
                      animate={{ scale: isSelected ? 1.18 : 1 }}
                      transition={{ duration: 0.55 }}
                      style={{ fontSize: 44, marginBottom: 22, opacity: 0.88 }}
                    >
                      {cat.icon}
                    </motion.div>
                    <h4 style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: isSelected ? 48 : 40,
                      fontWeight: 300,
                      color: '#1C1409',
                      margin: '0 0 16px 0',
                      transition: 'font-size 0.5s cubic-bezier(0.16,1,0.3,1)',
                      lineHeight: 1.1,
                    }}>
                      {cat.title}
                    </h4>
                    <p style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 14,
                      color: 'rgba(28,20,9,0.52)',
                      lineHeight: 1.75,
                      maxWidth: 340,
                      margin: '0 auto 28px',
                    }}>
                      {cat.desc}
                    </p>
                    <span style={{
                      fontSize: 11,
                      letterSpacing: '3px',
                      textTransform: 'uppercase',
                      color: '#9A6E00',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      {cat.price}
                    </span>

                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ opacity: 0, y: 18 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.55, delay: 0.38, ease: [0.16, 1, 0.3, 1] }}
                          style={{ marginTop: 36 }}
                        >
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '12px 30px',
                            border: '1px solid rgba(184,134,11,0.45)',
                            borderRadius: 2,
                            color: '#9A6E00',
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 10,
                            letterSpacing: '3px',
                            textTransform: 'uppercase',
                          }}>
                            Selected ✓
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div
                    key="shrunk"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, delay: 0.22 }}
                    style={{
                      padding: '28px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 14,
                      height: '100%',
                      minHeight: 240,
                    }}
                  >
                    <div style={{ fontSize: 18, opacity: 0.3 }}>{cat.icon}</div>
                    <div style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 9,
                      letterSpacing: '3px',
                      textTransform: 'uppercase',
                      color: 'rgba(28,20,9,0.3)',
                      animation: 'orbDrift 3.8s ease-in-out infinite',
                    }}>
                      {cat.title}
                    </div>
                    <div style={{ width: 20, height: 1, background: 'rgba(184,134,11,0.28)' }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

// ─── PRACTICE SUB-SERVICES ────────────────────────────────────────────────────
function PracticeSubCards({ subService, onSelect }) {
  const [burst, setBurst] = useState(null);

  return (
    <>
      {burst && <ParticleBurst x={burst.x} y={burst.y} />}
      <motion.div
        initial={{ opacity: 0, x: -28 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
        style={{ textAlign: 'center' }}
      >
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '4px',
          color: '#9A6E00',
          marginBottom: 36,
        }}>
          Select Practice Type
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
          maxWidth: 720,
          margin: '0 auto',
        }}>
          {PRACTICE_SUB_SERVICES.map((sub, i) => {
            const active = subService?.id === sub.id;
            return (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -5, transition: { duration: 0.3 } }}
                onClick={(e) => {
                  setBurst({ x: e.clientX, y: e.clientY });
                  setTimeout(() => setBurst(null), 900);
                  onSelect(sub);
                }}
                style={{
                  padding: '36px 28px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: active ? 'rgba(212,160,23,0.09)' : 'rgba(255,251,244,0.75)',
                  border: active ? '1px solid rgba(184,134,11,0.55)' : '1px solid rgba(184,134,11,0.12)',
                  boxShadow: active
                    ? '0 8px 32px rgba(184,134,11,0.12)'
                    : '0 2px 12px rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(8px)',
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'center',
                  transition: 'border-color 0.4s, background 0.4s, box-shadow 0.4s',
                }}
              >
                {active && (
                  <motion.div
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 0.9 }}
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'radial-gradient(ellipse at center, rgba(212,160,23,0.16) 0%, transparent 70%)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <h4 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 30,
                  fontWeight: 300,
                  color: '#1C1409',
                  marginBottom: 12,
                }}>
                  {sub.title}
                </h4>
                <p style={{
                  fontSize: 13,
                  color: 'rgba(28,20,9,0.48)',
                  marginBottom: 20,
                  lineHeight: 1.65,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {sub.desc}
                </p>
                <span style={{
                  color: '#9A6E00',
                  fontSize: 11,
                  letterSpacing: '2px',
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {sub.price}
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </>
  );
}

// ─── DATE CALENDAR ────────────────────────────────────────────────────────────
function DateCalendar({ date, onSelect }) {
  const [trailCells, setTrailCells] = useState([]);
  const [burst, setBurst] = useState(null);

  const handleDateClick = (d, e) => {
    if (date && date.date !== d.date) {
      const prevIdx = DATES.findIndex(x => x.date === date.date);
      const newIdx = DATES.findIndex(x => x.date === d.date);
      const min = Math.min(prevIdx, newIdx);
      const max = Math.max(prevIdx, newIdx);
      setTrailCells(DATES.slice(min, max + 1).map(x => x.date));
      setTimeout(() => setTrailCells([]), 700);
    }
    setBurst({ x: e.clientX, y: e.clientY });
    setTimeout(() => setBurst(null), 900);
    onSelect(d);
  };

  return (
    <>
      {burst && <ParticleBurst x={burst.x} y={burst.y} />}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 10,
          background: 'rgba(255,251,244,0.65)',
          backdropFilter: 'blur(12px)',
          padding: '28px',
          borderRadius: 4,
          border: '1px solid rgba(184,134,11,0.12)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          position: 'relative',
        }}>
          {DATES.map((d) => {
            const isActive = date?.date === d.date;
            const isTrail = trailCells.includes(d.date) && !isActive;

            return (
              <motion.div
                key={d.date}
                className={`date-cell${isActive ? ' date-active' : ''}`}
                onClick={(e) => handleDateClick(d, e)}
                animate={{
                  background: isActive
                    ? '#B8860B'
                    : isTrail
                      ? 'rgba(184,134,11,0.1)'
                      : 'rgba(28,20,9,0.02)',
                  scale: isActive ? 1.06 : 1,
                }}
                whileHover={!isActive ? {
                  background: 'rgba(184,134,11,0.08)',
                  scale: 1.05,
                  transition: { duration: 0.2 },
                } : {}}
                transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  padding: '20px 0',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  color: isActive ? '#FAF6EF' : '#1C1409',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div className="date-sonar" />
                <span style={{
                  display: 'block',
                  fontSize: 9,
                  fontFamily: "'DM Sans', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: 7,
                  opacity: isActive ? 0.85 : 0.38,
                }}>
                  {d.day}
                </span>
                <span style={{
                  fontSize: 26,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 300,
                }}>
                  {d.date}
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </>
  );
}

// ─── TIME SLOTS — MAGNETIC ────────────────────────────────────────────────────
function TimeSlots({ time, onSelect }) {
  const [burst, setBurst] = useState(null);
  const slotRefs = useRef({});

  const handleMouseMove = (t, e) => {
    const el = slotRefs.current[t];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.16;
    const dy = (e.clientY - (r.top + r.height / 2)) * 0.16;
    el.style.transform = `translate(${dx}px, ${dy}px) scale(1.05)`;
    el.style.transition = 'transform 0.06s';
    TIMESLOTS.forEach((ot) => {
      if (ot === t) return;
      const oel = slotRefs.current[ot];
      if (!oel) return;
      const or = oel.getBoundingClientRect();
      const odx = (or.left + or.width / 2 - e.clientX) * 0.04;
      const ody = (or.top + or.height / 2 - e.clientY) * 0.04;
      oel.style.transition = 'transform 0.12s';
      oel.style.transform = `translate(${odx}px, ${ody}px)`;
    });
  };

  const handleMouseLeave = () => {
    TIMESLOTS.forEach((t) => {
      const el = slotRefs.current[t];
      if (el) {
        el.style.transition = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)';
        el.style.transform = '';
        setTimeout(() => { if (el) el.style.transition = ''; }, 500);
      }
    });
  };

  const handleClick = (t, e) => {
    setBurst({ x: e.clientX, y: e.clientY });
    setTimeout(() => setBurst(null), 900);
    onSelect(t);
    const el = slotRefs.current[t];
    if (el) {
      el.style.transition = 'transform 0.12s ease-out';
      el.style.transform = 'scale(1.18)';
      setTimeout(() => {
        el.style.transition = 'transform 0.45s cubic-bezier(0.16,1,0.3,1)';
        el.style.transform = '';
        setTimeout(() => { el.style.transition = ''; }, 450);
      }, 120);
    }
  };

  return (
    <>
      {burst && <ParticleBurst x={burst.x} y={burst.y} />}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}
        onMouseLeave={handleMouseLeave}
      >
        {TIMESLOTS.map((t) => {
          const isActive = time === t;
          return (
            <div
              key={t}
              ref={(el) => { slotRefs.current[t] = el; }}
              className={`slot-pill${isActive ? ' active-slot' : ''}`}
              onMouseMove={(e) => handleMouseMove(t, e)}
              onClick={(e) => handleClick(t, e)}
              style={{
                padding: '18px 52px',
                borderRadius: 40,
                border: isActive ? '1px solid #B8860B' : '1px solid rgba(28,20,9,0.14)',
                background: isActive ? '#B8860B' : 'rgba(255,251,244,0.8)',
                backdropFilter: 'blur(8px)',
                color: isActive ? '#FAF6EF' : '#1C1409',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                letterSpacing: '2px',
                userSelect: 'none',
                boxShadow: isActive
                  ? '0 4px 20px rgba(184,134,11,0.22)'
                  : '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              {t}
            </div>
          );
        })}
      </motion.div>
    </>
  );
}

// ─── CONFIRMATION ─────────────────────────────────────────────────────────────
function ConfirmationScreen({ finalService, date, time, userName, userPhone, userEmail, onReturn, confCode }) {
  const [phase, setPhase] = useState('curtain');
  const [confirmCode] = useState(() => confCode || String(1000 + Math.floor(Math.random() * 9000)));

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('draw'), 850);
    const t2 = setTimeout(() => setPhase('text'), 2100);
    const t3 = setTimeout(() => setPhase('done'), 2900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const heading = 'Booking Confirmed';

  return (
    <div style={{ position: 'relative', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AnimatePresence>
        {phase === 'curtain' && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            exit={{ scaleX: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed', inset: 0,
              background: 'linear-gradient(90deg, #FAF6EF 0%, #F2E8D0 60%, rgba(212,160,23,0.2) 100%)',
              zIndex: 9998,
              pointerEvents: 'none',
              transformOrigin: phase === 'curtain' ? 'left' : 'right',
            }}
          />
        )}
      </AnimatePresence>

      {/* Burst particles */}
      {phase !== 'curtain' && (
        <div style={{ position: 'absolute', left: '50%', top: '15%', pointerEvents: 'none' }}>
          {Array.from({ length: 28 }, (_, i) => {
            const angle = (i / 28) * 360;
            const dist = 70 + Math.random() * 110;
            return (
              <motion.div
                key={i}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{ x: Math.cos((angle * Math.PI) / 180) * dist, y: Math.sin((angle * Math.PI) / 180) * dist - 50, opacity: 0, scale: 0 }}
                transition={{ duration: 1.5, delay: i * 0.022, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  width: i % 4 === 0 ? 4 : 2.5,
                  height: i % 4 === 0 ? 4 : 2.5,
                  borderRadius: '50%',
                  background: i % 3 === 0 ? '#D4A017' : '#B8860B',
                }}
              />
            );
          })}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={phase !== 'curtain' ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: '100%', maxWidth: 620, margin: '0 auto',
          textAlign: 'center', padding: '52px 44px',
          border: '1px solid rgba(184,134,11,0.2)',
          background: 'rgba(255,251,244,0.88)',
          backdropFilter: 'blur(16px)',
          borderRadius: 4,
          boxShadow: '0 16px 64px rgba(0,0,0,0.08)',
          position: 'relative', zIndex: 10,
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <svg width="68" height="68" viewBox="0 0 76 76" style={{ overflow: 'visible' }}>
            <circle
              cx="38" cy="38" r="32"
              fill="none" stroke="#B8860B" strokeWidth="1"
              strokeDasharray="201" strokeDashoffset="201"
              style={{
                animation: phase !== 'curtain'
                  ? 'drawCircle 1.1s cubic-bezier(0.16,1,0.3,1) 0.15s forwards'
                  : 'none',
              }}
            />
            <path
              d="M24 38 L34 48 L52 30"
              fill="none" stroke="#B8860B" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray="50" strokeDashoffset="50"
              style={{
                animation: phase !== 'curtain'
                  ? 'drawCheck 0.65s cubic-bezier(0.16,1,0.3,1) 1.25s forwards'
                  : 'none',
              }}
            />
          </svg>
        </div>

        <span style={{
          fontSize: 10, letterSpacing: '4px', color: '#9A6E00',
          textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif",
          display: 'block', marginBottom: 18,
          opacity: phase === 'curtain' ? 0 : 1,
          transition: 'opacity 0.6s 0.4s',
        }}>
          Slot Reserved
        </span>

        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 300, color: '#1C1409',
          margin: '0 0 18px 0',
          display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {heading.split('').map((char, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                opacity: (phase === 'text' || phase === 'done') ? undefined : 0,
                animation: (phase === 'text' || phase === 'done')
                  ? `letterDrop 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 0.032}s both`
                  : 'none',
                whiteSpace: char === ' ' ? 'pre' : undefined,
              }}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </h2>

        {/* 4-digit Confirmation Code */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={phase === 'done' ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{
            display: 'inline-flex',
            gap: 8,
            marginBottom: 28,
            padding: '14px 28px',
            background: 'rgba(184,134,11,0.06)',
            border: '1px dashed rgba(184,134,11,0.3)',
            borderRadius: 6,
          }}
        >
          <span style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 10,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: 'rgba(28,20,9,0.4)',
            alignSelf: 'center',
          }}>Confirmation Code</span>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 32,
            fontWeight: 600,
            color: '#B8860B',
            letterSpacing: '6px',
          }}>{confirmCode}</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={phase === 'done' ? { opacity: 1 } : {}}
          transition={{ duration: 0.8 }}
          style={{
            fontFamily: "'DM Sans', sans-serif",
            color: 'rgba(28,20,9,0.52)',
            lineHeight: 1.8, marginBottom: 28, fontSize: 13,
          }}
        >
          Your <strong style={{ color: '#1C1409', fontWeight: 400 }}>{finalService?.title}</strong> is tentatively
          booked for <strong style={{ color: '#1C1409', fontWeight: 400 }}>{date?.full?.toLocaleDateString()}</strong>{' '}
          at <strong style={{ color: '#1C1409', fontWeight: 400 }}>{time}</strong>.
          {userName && <><br />Booked by <strong style={{ color: '#1C1409', fontWeight: 400 }}>{userName}</strong> ({userPhone})</>}
          <br />Our studio manager will contact you shortly to confirm.
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={phase === 'done' ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.25 }}
          onClick={onReturn}
          className="confirm-btn"
          style={{ padding: '16px 44px', fontSize: 10, letterSpacing: '3px', borderRadius: 2 }}
        >
          Return to Home
        </motion.button>
      </motion.div>
    </div>
  );
}

// ─── DIMENSIONAL RIFT (transition overlay) ────────────────────────────────────────
function DimensionalRift({ origin, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 400);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 0.25, delay: 0.15 }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}
    >
      {/* Central rift light */}
      <motion.div
        initial={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          x: origin.x - 2,
          y: origin.y - 2,
          opacity: 1,
        }}
        animate={{
          width: '300vmax',
          height: '300vmax',
          borderRadius: '50%',
          x: origin.x - window.innerWidth * 1.5,
          y: origin.y - window.innerHeight * 1.5,
          opacity: 0.95,
        }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'absolute',
          background: 'radial-gradient(circle, rgba(212,160,23,0.3) 0%, rgba(250,246,239,0.95) 35%, #FAF6EF 60%)',
        }}
      />

      {/* Rift line - vertical split */}
      <motion.div
        initial={{ scaleY: 0, opacity: 1 }}
        animate={{ scaleY: 1, opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          left: origin.x - 1,
          top: 0,
          width: 2,
          height: '100vh',
          background: 'linear-gradient(to bottom, transparent, #D4A017, transparent)',
          transformOrigin: `center ${origin.y}px`,
        }}
      />

      {/* Gold sparks from origin */}
      {Array.from({ length: 16 }, (_, i) => {
        const angle = (i / 16) * 360;
        const dist = 60 + Math.random() * 140;
        return (
          <motion.div
            key={i}
            initial={{ x: origin.x, y: origin.y, opacity: 1, scale: 1 }}
            animate={{
              x: origin.x + Math.cos((angle * Math.PI) / 180) * dist,
              y: origin.y + Math.sin((angle * Math.PI) / 180) * dist,
              opacity: 0,
              scale: 0,
            }}
            transition={{ duration: 0.35, delay: i * 0.008, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: i % 3 === 0 ? 5 : 3,
              height: i % 3 === 0 ? 5 : 3,
              borderRadius: '50%',
              background: i % 2 === 0 ? '#D4A017' : '#B8860B',
            }}
          />
        );
      })}
    </motion.div>
  );
}

// ─── SCENE WRAPPER ────────────────────────────────────────────────────────────
const sceneVariants = {
  initial: { opacity: 0, scale: 0.9, filter: 'blur(10px)' },
  enter: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1], staggerChildren: 0.03, delayChildren: 0.05 },
  },
  exit: {
    opacity: 0,
    scale: 1.2,
    filter: 'blur(15px)',
    transition: { duration: 0.35, ease: [0.33, 1, 0.68, 1] },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 14 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
};

// ─── MAIN FLOW (full-viewport scene transitions) ─────────────────────────────
function StudioFlow() {
  /* State machine: service → subservice (practice only) → date → slot → details → confirm */
  const [step, setStep] = useState('service');
  const [category, setCategory] = useState(null);
  const [subService, setSubService] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [rift, setRift] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [confCode, setConfCode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const finalService = category?.id === 'recording' ? category : subService;

  const triggerTransition = (e, nextStep) => {
    // If there's no event (like when triggered artificially), use center of screen
    const origin = e ? { x: e.clientX, y: e.clientY } : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    setRift({ origin });
    setStep(nextStep);
    // Auto-clear rift after animation
  };

  const handleCategorySelect = (cat, e) => {
    setCategory(cat);
    setSubService(null);
    setDate(null);
    setTime(null);
    triggerTransition(e, cat.id === 'recording' ? 'date' : 'subservice');
  };

  const handleSubSelect = (sub, e) => {
    setSubService(sub);
    setDate(null);
    setTime(null);
    triggerTransition(e, 'date');
  };

  const handleDateSelect = async (d, e) => {
    setDate(d);
    setTime(null);
    triggerTransition(e, 'slot');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${API_URL}/api/bookings/slots?date=${d.full.toISOString()}`);
      if (res.ok) {
        const data = await res.json();
        setBookedSlots(data.bookedSlots || []);
      }
    } catch (err) {
      console.error('Failed to fetch slots', err);
      setBookedSlots([]);
    }
  };

  const handleTimeSelect = (t, e) => {
    setTime(t);
    triggerTransition(e, 'details');
  };

  const handleDetailsSubmit = async (e) => {
    if (isSubmitting) return;
    const code = String(1000 + Math.floor(Math.random() * 9000));
    setConfCode(code);
    setIsSubmitting(true);

    try {
      const payload = {
        service: finalService?.title,
        serviceCategory: category?.id,
        date: date?.full,
        timeSlot: time,
        userName,
        userPhone,
        userEmail,
        confirmationCode: code,
        price: finalService?.price
      };

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('Booking failed', err);
    } finally {
      setIsSubmitting(false);
      triggerTransition(e, 'confirm');
    }
  };

  const handleBack = (toStep) => {
    setStep(toStep);
    if (toStep === 'service') {
      setCategory(null); setSubService(null); setDate(null); setTime(null);
    } else if (toStep === 'subservice') {
      setSubService(null); setDate(null); setTime(null);
    } else if (toStep === 'date') {
      setDate(null); setTime(null);
    } else if (toStep === 'slot') {
      setTime(null);
    }
  };

  const headingStyle = {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(32px, 8vw, 64px)',
    fontWeight: 300,
    color: '#9A6E00',
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: 56,
    lineHeight: 1.1,
    marginTop: 8,
    width: '100%',
    textAlign: 'center',
  };

  const sceneContainer = {
    minHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 20px',
  };

  const backBtnStyle = {
    alignSelf: 'flex-start',
    marginBottom: 20,
    background: 'none',
    border: '1px solid rgba(184,134,11,0.2)',
    borderRadius: 40,
    padding: '10px 24px',
    fontSize: 11,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#9A6E00',
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
    transition: 'all 0.3s',
    backdropFilter: 'blur(8px)',
  };

  return (
    <>
      {rift && <DimensionalRift origin={rift.origin} onDismiss={() => setRift(null)} />}

      <AnimatePresence mode="popLayout">
        {/* ── STEP 1: Select Service ── */}
        {step === 'service' && (
          <motion.div
            key="service"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <motion.div variants={itemVariants} style={{ textAlign: 'center', width: '100%' }}>
              <motion.p
                variants={itemVariants}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  color: 'rgba(28,20,9,0.3)',
                  marginBottom: 12,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Step 01
              </motion.p>
              <motion.h3 variants={itemVariants} style={headingStyle}>
                Select Service
              </motion.h3>
            </motion.div>

            <motion.div variants={itemVariants} style={{ width: '100%' }}>
              <ImmersiveServiceCards onSelect={handleCategorySelect} />
            </motion.div>
          </motion.div>
        )}

        {/* ── STEP 1b: Practice Sub-Service ── */}
        {step === 'subservice' && (
          <motion.div
            key="subservice"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <motion.button
              variants={itemVariants}
              style={backBtnStyle}
              onClick={() => handleBack('service')}
              whileHover={{ borderColor: '#B8860B', background: 'rgba(184,134,11,0.05)' }}
            >
              ← Back
            </motion.button>

            <motion.div variants={itemVariants} style={{ textAlign: 'center', width: '100%' }}>
              <motion.div
                variants={itemVariants}
                style={{
                  fontSize: 36, marginBottom: 16, opacity: 0.6,
                }}
              >
                🎸
              </motion.div>
              <motion.p
                variants={itemVariants}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: '5px',
                  textTransform: 'uppercase',
                  color: 'rgba(28,20,9,0.35)',
                  marginBottom: 20,
                }}
              >
                Practice Session
              </motion.p>
              <motion.h3 variants={itemVariants} style={headingStyle}>
                Choose Your Style
              </motion.h3>
            </motion.div>

            <motion.div variants={itemVariants} style={{ width: '100%' }}>
              <ImmersivePracticeCards onSelect={handleSubSelect} />
            </motion.div>
          </motion.div>
        )}

        {/* ── STEP 2: Choose Date ── */}
        {step === 'date' && (
          <motion.div
            key="date"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <motion.button
              variants={itemVariants}
              style={backBtnStyle}
              onClick={() => handleBack(category?.id === 'practice' ? 'subservice' : 'service')}
              whileHover={{ borderColor: '#B8860B', background: 'rgba(184,134,11,0.05)' }}
            >
              ← Back
            </motion.button>

            {/* Selected service badge */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 24px',
                border: '1px solid rgba(184,134,11,0.2)',
                borderRadius: 40,
                marginBottom: 28,
                backdropFilter: 'blur(8px)',
                background: 'rgba(255,251,244,0.6)',
              }}
            >
              <span style={{ fontSize: 16 }}>{finalService?.icon || '🎵'}</span>
              <span style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: '#9A6E00',
              }}>
                {finalService?.title}
              </span>
            </motion.div>

            <motion.div variants={itemVariants} style={{ textAlign: 'center', width: '100%' }}>
              <motion.p
                variants={itemVariants}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: '5px',
                  textTransform: 'uppercase',
                  color: 'rgba(28,20,9,0.35)',
                  marginBottom: 20,
                }}
              >
                Step 02
              </motion.p>
              <motion.h3 variants={itemVariants} style={headingStyle}>
                Choose Date
              </motion.h3>
            </motion.div>

            <motion.div variants={itemVariants} style={{ width: '100%' }}>
              <ImmersiveDateCalendar onSelect={handleDateSelect} />
            </motion.div>
          </motion.div>
        )}

        {/* ── STEP 3: Select Slot ── */}
        {step === 'slot' && (
          <motion.div
            key="slot"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <motion.button
              variants={itemVariants}
              style={backBtnStyle}
              onClick={() => handleBack('date')}
              whileHover={{ borderColor: '#B8860B', background: 'rgba(184,134,11,0.05)' }}
            >
              ← Back
            </motion.button>

            {/* Context badges */}
            <motion.div variants={itemVariants} style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 20px', border: '1px solid rgba(184,134,11,0.15)',
                borderRadius: 40, background: 'rgba(255,251,244,0.6)', backdropFilter: 'blur(8px)',
              }}>
                <span style={{ fontSize: 14 }}>{finalService?.icon || '🎵'}</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9A6E00' }}>
                  {finalService?.title}
                </span>
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 20px', border: '1px solid rgba(184,134,11,0.15)',
                borderRadius: 40, background: 'rgba(255,251,244,0.6)', backdropFilter: 'blur(8px)',
              }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9A6E00' }}>
                  📅 {date?.full?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} style={{ textAlign: 'center', width: '100%' }}>
              <motion.p
                variants={itemVariants}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: '5px',
                  textTransform: 'uppercase',
                  color: 'rgba(28,20,9,0.35)',
                  marginBottom: 20,
                }}
              >
                Step 03
              </motion.p>
              <motion.h3 variants={itemVariants} style={headingStyle}>
                Select Slot
              </motion.h3>
            </motion.div>

            <motion.div variants={itemVariants} style={{ width: '100%' }}>
              <ImmersiveTimeSlots onSelect={handleTimeSelect} bookedSlots={bookedSlots} />
            </motion.div>
          </motion.div>
        )}

        {/* ── STEP 4: Your Details ── */}
        {step === 'details' && (
          <motion.div
            key="details"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <motion.button
              variants={itemVariants}
              style={backBtnStyle}
              onClick={() => handleBack('slot')}
              whileHover={{ borderColor: '#B8860B', background: 'rgba(184,134,11,0.05)' }}
            >
              ← Back
            </motion.button>

            {/* Context badges */}
            <motion.div variants={itemVariants} style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 20px', border: '1px solid rgba(184,134,11,0.15)',
                borderRadius: 40, background: 'rgba(255,251,244,0.6)', backdropFilter: 'blur(8px)',
              }}>
                <span style={{ fontSize: 14 }}>{finalService?.icon || '🎵'}</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9A6E00' }}>
                  {finalService?.title}
                </span>
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 20px', border: '1px solid rgba(184,134,11,0.15)',
                borderRadius: 40, background: 'rgba(255,251,244,0.6)', backdropFilter: 'blur(8px)',
              }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#9A6E00' }}>
                  📅 {date?.full?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {time}
                </span>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} style={{ textAlign: 'center', width: '100%' }}>
              <motion.p
                variants={itemVariants}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: '5px',
                  textTransform: 'uppercase',
                  color: 'rgba(28,20,9,0.35)',
                  marginBottom: 20,
                }}
              >
                Step 04
              </motion.p>
              <motion.h3 variants={itemVariants} style={headingStyle}>
                Your Details
              </motion.h3>
            </motion.div>

            <motion.div variants={itemVariants} style={{ width: '100%', maxWidth: 520, margin: '0 auto' }}>
              <UserDetailsForm
                name={userName} phone={userPhone} email={userEmail}
                onNameChange={setUserName} onPhoneChange={setUserPhone} onEmailChange={setUserEmail}
                onSubmit={handleDetailsSubmit}
              />
            </motion.div>
          </motion.div>
        )}

        {/* ── STEP 5: Confirmation ── */}
        {step === 'confirm' && (
          <motion.div
            key="confirm"
            variants={sceneVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={sceneContainer}
          >
            <ConfirmationScreen
              finalService={finalService}
              date={date}
              time={time}
              userName={userName}
              userPhone={userPhone}
              userEmail={userEmail}
              confCode={confCode}
              onReturn={() => (window.location.href = '/')}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── USER DETAILS FORM ────────────────────────────────────────────────────────
function UserDetailsForm({ name, phone, email, onNameChange, onPhoneChange, onEmailChange, onSubmit }) {
  const isValid = name.trim().length >= 2 && phone.trim().length >= 10;

  const inputStyle = {
    width: '100%',
    padding: '16px 20px',
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    background: 'rgba(255,251,244,0.8)',
    border: '1px solid rgba(184,134,11,0.15)',
    borderRadius: 4,
    color: '#1C1409',
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s',
    backdropFilter: 'blur(8px)',
  };

  const labelStyle = {
    display: 'block',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 10,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#9A6E00',
    marginBottom: 8,
    textAlign: 'left',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        padding: 'clamp(24px, 8vw, 40px) clamp(16px, 8vw, 36px)',
        background: 'rgba(255,251,244,0.7)',
        border: '1px solid rgba(184,134,11,0.12)',
        borderRadius: 6,
        backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label style={labelStyle}>Full Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter your full name"
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = '#B8860B'; e.target.style.boxShadow = '0 0 0 3px rgba(184,134,11,0.08)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(184,134,11,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone Number *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="+91 XXXXX XXXXX"
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = '#B8860B'; e.target.style.boxShadow = '0 0 0 3px rgba(184,134,11,0.08)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(184,134,11,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
        <div>
          <label style={labelStyle}>Email (Optional)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="your@email.com"
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = '#B8860B'; e.target.style.boxShadow = '0 0 0 3px rgba(184,134,11,0.08)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(184,134,11,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
      </div>

      <motion.button
        whileHover={isValid ? { y: -2, boxShadow: '0 10px 48px rgba(184,134,11,0.22)' } : {}}
        whileTap={isValid ? { scale: 0.98 } : {}}
        onClick={(e) => { if (isValid) onSubmit(e); }}
        className="confirm-btn"
        style={{
          width: '100%',
          marginTop: 28,
          padding: '18px',
          fontSize: 11,
          letterSpacing: '3px',
          textTransform: 'uppercase',
          borderRadius: 2,
          opacity: isValid ? 1 : 0.4,
          cursor: isValid ? 'pointer' : 'not-allowed',
        }}
      >
        Confirm Booking →
      </motion.button>

      <p style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 11,
        color: 'rgba(28,20,9,0.3)',
        textAlign: 'center',
        marginTop: 16,
        lineHeight: 1.6,
      }}>
        Your details will be shared with our studio manager for booking confirmation.
      </p>
    </motion.div>
  );
}

// ─── IMMERSIVE SERVICE CARDS (full-screen, click passes event) ─────────────────
function ImmersiveServiceCards({ onSelect }) {
  const tiltRef = useRef({});

  const onMouseMove = (id, e) => {
    const el = tiltRef.current[id];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 10;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -10;
    el.style.transform = `perspective(1200px) rotateY(${x}deg) rotateX(${y}deg) translateZ(12px) scale(1.02)`;
    el.style.transition = 'transform 0.05s';
  };

  const onMouseLeave = (id) => {
    const el = tiltRef.current[id];
    if (!el) return;
    el.style.transition = 'transform 0.65s cubic-bezier(0.16,1,0.3,1)';
    el.style.transform = '';
    setTimeout(() => { if (el) el.style.transition = ''; }, 650);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 24,
      maxWidth: 900,
      margin: '0 auto',
      padding: '0 20px',
    }}>
      {STUDIO_CATEGORIES.map((cat, i) => (
        <motion.div
          key={cat.id}
          ref={(el) => { tiltRef.current[cat.id] = el; }}
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.05, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          onMouseMove={(e) => onMouseMove(cat.id, e)}
          onMouseLeave={() => onMouseLeave(cat.id)}
          onClick={(e) => onSelect(cat, e)}
          style={{
            padding: 'clamp(32px, 8vw, 64px) clamp(20px, 5vw, 40px)',
            borderRadius: 6,
            cursor: 'pointer',
            border: '1px solid rgba(184,134,11,0.14)',
            background: 'rgba(255,251,244,0.72)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 32px rgba(0,0,0,0.06)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Hover glow */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse at center, rgba(212,160,23,0.08) 0%, transparent 70%)',
            opacity: 0,
            transition: 'opacity 0.4s',
            pointerEvents: 'none',
          }}
            className="card-glow"
          />

          <div style={{ fontSize: 52, marginBottom: 28, opacity: 0.85 }}>{cat.icon}</div>
          <h4 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 38,
            fontWeight: 300,
            color: '#1C1409',
            margin: '0 0 16px 0',
            lineHeight: 1.1,
          }}>
            {cat.title}
          </h4>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            color: 'rgba(28,20,9,0.48)',
            lineHeight: 1.75,
            maxWidth: 320,
            margin: '0 auto 28px',
          }}>
            {cat.desc}
          </p>
          <span style={{
            fontSize: 11,
            letterSpacing: '3px',
            textTransform: 'uppercase',
            color: '#9A6E00',
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {cat.price}
          </span>

          {/* Arrow indicator */}
          <motion.div
            style={{
              marginTop: 36,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: '#B8860B',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 10,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            Select & Continue →
          </motion.div>
        </motion.div>
      ))}

      <style>{`
        div:hover > .card-glow { opacity: 1 !important; }
      `}</style>
    </div>
  );
}

// ─── IMMERSIVE PRACTICE CARDS ─────────────────────────────────────────────────
function ImmersivePracticeCards({ onSelect }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: 20,
      width: '100%',
      maxWidth: 780,
      margin: '0 auto',
      padding: '0 16px',
    }}>
      {PRACTICE_SUB_SERVICES.map((sub, i) => (
        <motion.div
          key={sub.id}
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.05, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{
            y: -8,
            boxShadow: '0 16px 48px rgba(184,134,11,0.15)',
            borderColor: 'rgba(184,134,11,0.4)',
            transition: { duration: 0.3 },
          }}
          onClick={(e) => onSelect(sub, e)}
          style={{
            padding: 'clamp(36px, 10vw, 56px) clamp(20px, 5vw, 36px)',
            borderRadius: 6,
            cursor: 'pointer',
            background: 'rgba(255,251,244,0.75)',
            border: '1px solid rgba(184,134,11,0.12)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 20, opacity: 0.7 }}>
            {sub.id === 'live' ? '🎸' : '🎤'}
          </div>
          <h4 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 34,
            fontWeight: 300,
            color: '#1C1409',
            marginBottom: 14,
          }}>
            {sub.title}
          </h4>
          <p style={{
            fontSize: 13,
            color: 'rgba(28,20,9,0.48)',
            marginBottom: 24,
            lineHeight: 1.7,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {sub.desc}
          </p>
          <span style={{
            color: '#9A6E00',
            fontSize: 12,
            letterSpacing: '2px',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 500,
          }}>
            {sub.price}
          </span>
          <div style={{
            marginTop: 28,
            color: '#B8860B',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 10,
            letterSpacing: '3px',
            textTransform: 'uppercase',
            opacity: 0.5,
          }}>
            Select →
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// ─── IMMERSIVE DATE CALENDAR ──────────────────────────────────────────────────
function ImmersiveDateCalendar({ onSelect }) {
  const [hoveredDate, setHoveredDate] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <div
        className="date-calendar-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 10,
          background: 'rgba(255,251,244,0.65)',
          backdropFilter: 'blur(12px)',
          padding: 'clamp(12px, 3vw, 28px)',
          borderRadius: 6,
          border: '1px solid rgba(184,134,11,0.12)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
          maxWidth: 760,
          margin: '0 auto',
          width: '100%',
        }}
      >
        {DATES.map((d, i) => {
          const isHovered = hoveredDate === d.date;
          return (
            <motion.div
              key={d.date}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 * i, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => setHoveredDate(d.date)}
              onMouseLeave={() => setHoveredDate(null)}
              onClick={(e) => onSelect(d, e)}
              whileHover={{ scale: 1.08, transition: { duration: 0.2 } }}
              style={{
                padding: 'clamp(12px, 3vw, 20px) 0',
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 4,
                background: isHovered ? 'rgba(184,134,11,0.08)' : 'rgba(28,20,9,0.02)',
                border: isHovered ? '1px solid rgba(184,134,11,0.3)' : '1px solid transparent',
                transition: 'background 0.3s, border-color 0.3s',
              }}
            >
              <span style={{
                display: 'block',
                fontSize: 9,
                fontFamily: "'DM Sans', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: 7,
                opacity: 0.38,
                color: '#1C1409',
              }}>
                {d.day}
              </span>
              <span style={{
                fontSize: 'clamp(18px, 5vw, 26px)',
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                color: '#1C1409',
              }}>
                {d.date}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── IMMERSIVE TIME SLOTS ─────────────────────────────────────────────────────
function ImmersiveTimeSlots({ onSelect, bookedSlots = [] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      style={{ display: 'flex', gap: 'clamp(12px, 3vw, 24px)', justifyContent: 'center', flexWrap: 'wrap', padding: '0 12px' }}
    >
      {TIMESLOTS.map((t, i) => {
        const isBooked = bookedSlots.includes(t);
        return (
          <motion.div
            key={t}
            initial={{ opacity: 0, y: 15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.04 + i * 0.04, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            whileHover={isBooked ? {} : {
              scale: 1.08,
              y: -6,
              boxShadow: '0 12px 36px rgba(184,134,11,0.18)',
              borderColor: '#B8860B',
              transition: { duration: 0.25 },
            }}
            whileTap={isBooked ? {} : { scale: 0.95 }}
            onClick={(e) => !isBooked && onSelect(t, e)}
            style={{
              padding: 'clamp(16px, 4vw, 28px) clamp(24px, 12vw, 64px)',
              borderRadius: 50,
              border: '1px solid rgba(28,20,9,0.14)',
              background: 'rgba(255,251,244,0.8)',
              backdropFilter: 'blur(8px)',
              color: '#1C1409',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(13px, 4vw, 15px)',
              letterSpacing: 'clamp(1px, 1vw, 3px)',
              cursor: isBooked ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              transition: 'border-color 0.3s',
              opacity: isBooked ? 0.4 : 1,
            }}
          >
            {t} {isBooked && <span style={{ color: '#D85A30', fontSize: 10, letterSpacing: 1, marginLeft: 8 }}>FULL</span>}
          </motion.div>
        );
      })}
    </motion.div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function BookingPortal() {
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div
        className="booking-grain"
        style={{
          background: '#FAF6EF',
          minHeight: '100vh',
          color: '#1C1409',
          overflowX: 'hidden',
          width: '100%',
          position: 'relative',
        }}
      >
        <PianoBackground />
        <AmbientParticles />
        <Navbar />
        <div style={{
          paddingTop: 100,
          position: 'relative',
          zIndex: 10,
          minHeight: 'calc(100vh - 100px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <StudioFlow />
        </div>
      </div>
    </>
  );
}

